import React, { useState, useContext, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Container,
  CssBaseline,
  TextField,
  Button,
  InputAdornment,
  IconButton,
} from "@material-ui/core";
import { Visibility, VisibilityOff, Lock as LockIcon } from "@material-ui/icons";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ColorModeContext from "../../layout/themeContext";
import useSettings from "../../hooks/useSettings";
import { getBackendUrl } from "../../config";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100vw",
    height: "100vh",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    padding: "0",
    boxSizing: "border-box",
    overflow: "auto",
    position: "relative",
    backgroundColor: theme.palette.background ? theme.palette.background.default : (theme.palette.type === "light" ? "#f5f5f5" : "#303030"),
    backgroundImage: "none",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    backgroundPosition: "center center",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },

  "@keyframes slideInUp": {
    from: {
      opacity: 0,
      transform: "translateY(30px)",
    },
    to: {
      opacity: 1,
      transform: "translateY(0)",
    },
  },

  container: {
    padding: "16px",
    width: "80%",
    maxWidth: "500px",
    boxSizing: "border-box",
    zIndex: 10,
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    position: "fixed",
    margin: "0 auto",
    [theme.breakpoints.down("md")]: {
      width: "90%",
    },
    [theme.breakpoints.down("sm")]: {
      width: "95%",
    },
  },

  paper: {
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    boxShadow: `
      0 25px 50px rgba(0, 0, 0, 0.15),
      0 10px 25px rgba(0, 0, 0, 0.1),
      0 1px 0 rgba(255, 255, 255, 0.2) inset,
      0 0 0 1px rgba(255, 255, 255, 0.1)
    `,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "50px 40px",
    borderRadius: "20px",
    width: "80%",
    boxSizing: "border-box",
    margin: "0 auto",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    animation: "$slideInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    maxWidth: "500px",
    [theme.breakpoints.down("md")]: {
      width: "90%",
      padding: "40px 30px",
      borderRadius: "16px",
    },
    [theme.breakpoints.down("sm")]: {
      width: "95%",
      padding: "30px 20px",
      borderRadius: "14px",
      boxShadow: `0 15px 30px rgba(0, 0, 0, 0.12)`,
    },
  },

  logoImg: {
    width: "100%",
    maxWidth: "280px",
    height: "auto",
    maxHeight: "80px",
    margin: "0 auto 20px auto",
    filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
    content: "url(" + (theme.mode === "light"
      ? theme.calculatedLogoLight()
      : theme.calculatedLogoDark()) + ")",
  },

  iconContainer: {
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    background: `linear-gradient(135deg, ${theme.palette.primary.main}20 0%, ${theme.palette.primary.main}40 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 20px",
  },

  icon: {
    fontSize: "32px",
    color: theme.palette.primary.main,
  },

  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#1a1a1a",
    marginBottom: "8px",
    textAlign: "center",
  },

  subtitle: {
    fontSize: "14px",
    color: "#666",
    marginBottom: "32px",
    textAlign: "center",
  },

  form: {
    width: "100%",
    marginTop: theme.spacing(1),
  },

  textField: {
    marginBottom: "16px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      backdropFilter: "blur(10px)",
      transition: "all 0.3s ease",
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.9)",
      },
      "&.Mui-focused": {
        backgroundColor: "rgba(255, 255, 255, 1)",
        boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
      },
      "& fieldset": {
        borderColor: "rgba(59, 130, 246, 0.2)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(59, 130, 246, 0.4)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#3b82f6",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: "#6b7280",
      fontWeight: 500,
      "&.Mui-focused": {
        color: "#3b82f6",
      },
    },
  },

  submit: {
    margin: theme.spacing(3, 0, 2),
    background: theme.palette.primary.main,
    color: "white",
    borderRadius: "12px",
    padding: "14px 0",
    fontSize: "16px",
    fontWeight: 600,
    textTransform: "none",
    boxShadow: `0 4px 12px ${theme.palette.primary.main}66`,
    border: "none",
    transition: "all 0.3s ease",
    "&:hover": {
      background: theme.palette.primary.dark,
      transform: "translateY(-2px)",
      boxShadow: `0 6px 20px ${theme.palette.primary.main}99`,
    },
    "&:active": {
      transform: "translateY(0)",
    },
  },

  backButton: {
    width: "100%",
    padding: "14px 0",
    borderRadius: "12px",
    border: `2px solid ${theme.palette.primary.main}`,
    backgroundColor: "transparent",
    color: theme.palette.primary.main,
    fontSize: "16px",
    fontWeight: 600,
    cursor: "pointer",
    textDecoration: "none",
    display: "block",
    textAlign: "center",
    marginTop: "8px",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: `${theme.palette.primary.main}08`,
      borderColor: theme.palette.primary.dark,
      color: theme.palette.primary.dark,
    },
  },
}));

const ResetPassword = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const { colorMode } = React.useContext(ColorModeContext);
  const { appLogoFavicon, appName } = colorMode;
  const { getPublicSetting } = useSettings();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [backgroundLight, setBackgroundLight] = useState("");
  const [backgroundDark, setBackgroundDark] = useState("");

  // Extrair token da URL
  const query = new URLSearchParams(location.search);
  const token = query.get("token");

  const getCompanyIdFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const companyId = urlParams.get("companyId");
    return companyId ? parseInt(companyId) : null;
  };

  useEffect(() => {
    const companyId = getCompanyIdFromUrl();

    getPublicSetting("appLogoBackgroundLight", companyId)
      .then((bgLight) => {
        if (bgLight) {
          const backendUrl = getBackendUrl();
          const fullUrl = backendUrl + "/public/" + bgLight;
          setBackgroundLight(fullUrl);
        } else {
          setBackgroundLight("");
        }
      })
      .catch((err) => {
        console.error("Erro ao carregar imagem de fundo clara:", err);
        setBackgroundLight("");
      });

    getPublicSetting("appLogoBackgroundDark", companyId)
      .then((bgDark) => {
        if (bgDark) {
          const backendUrl = getBackendUrl();
          const fullUrl = backendUrl + "/public/" + bgDark;
          setBackgroundDark(fullUrl);
        } else {
          setBackgroundDark("");
        }
      })
      .catch((err) => {
        console.error("Erro ao carregar imagem de fundo escura:", err);
        setBackgroundDark("");
      });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      toast.error("Token inválido");
      return;
    }

    if (password.length < 6) {
      toast.error("A senha deve ter no mínimo 6 caracteres");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Senha redefinida com sucesso!");
      setTimeout(() => {
        history.push("/login");
      }, 2000);
    } catch (error) {
      if (error.response?.data?.error === "ERR_TOKEN_EXPIRED") {
        toast.error("Token expirado. Solicite uma nova recuperação de senha.");
      } else if (error.response?.data?.error === "ERR_INVALID_TOKEN") {
        toast.error("Token inválido. Solicite uma nova recuperação de senha.");
      } else {
        toast.error("Erro ao redefinir senha. Tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const { mode } = colorMode;
  const hasValidLightBg = backgroundLight && backgroundLight.trim() !== '';
  const hasValidDarkBg = backgroundDark && backgroundDark.trim() !== '';
  
  let finalBackground;
  if (mode === "light") {
    finalBackground = hasValidLightBg ? `url(${backgroundLight})` : (theme.palette.background ? theme.palette.background.default : "#f5f5f5");
  } else {
    finalBackground = hasValidDarkBg ? `url(${backgroundDark})` : (theme.palette.background ? theme.palette.background.default : "#303030");
  }

  return (
    <>
      <Helmet>
        <title>{appName || "Multi100"} - Redefinir Senha</title>
        <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"} />
      </Helmet>

      <div 
        className={classes.root}
        style={{
          backgroundImage: typeof finalBackground === "string" && finalBackground.includes("url(") 
            ? finalBackground 
            : "none"
        }}
      >
        <Container component="main" maxWidth={false} className={classes.container}>
          <CssBaseline />
          <div className={classes.paper}>
            <div>
              <img className={classes.logoImg} alt="logo" />
            </div>

            <div className={classes.iconContainer}>
              <LockIcon className={classes.icon} />
            </div>

            <h1 className={classes.title}>Redefinir Senha</h1>
            <p className={classes.subtitle}>
              Digite sua nova senha abaixo
            </p>

            <form className={classes.form} onSubmit={handleSubmit}>
              <TextField
                variant="outlined"
                required
                fullWidth
                name="password"
                label="Nova Senha"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={classes.textField}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                variant="outlined"
                required
                fullWidth
                name="confirmPassword"
                label="Confirmar Nova Senha"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={classes.textField}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
                disabled={loading}
              >
                {loading ? "Redefinindo..." : "Redefinir Senha"}
              </Button>

              <button
                type="button"
                className={classes.backButton}
                onClick={() => history.push("/login")}
              >
                Voltar para Login
              </button>
            </form>
          </div>
        </Container>
      </div>
    </>
  );
};

export default ResetPassword;
